import React from 'react';
import Link from 'next/link';
import {MoonLoader} from 'react-spinners';

import {useGetOutletsQuery} from '@graphql/generated/graphql';
import {useGeolocation} from 'context';
import {CartOutletType, useMixOrderStore} from 'stores';
import {BackBar, CartCard} from '@components/index';
import {getOutletItemsCount} from '@lib/index';

export type CartOutletsType = {
  outletFriendlyPath: string;
  cartOutlet: CartOutletType;
}[];

interface IMyCartProps {}

const MyCart: React.FC<IMyCartProps> = () => {
  const {latitude, longitude} = useGeolocation();
  const {data} = useGetOutletsQuery({variables: {latitude, longitude}, fetchPolicy: 'cache-and-network'});

  const cart = useMixOrderStore(s => s.cart);
  const removeCartOutlet = useMixOrderStore(s => s.removeCartOutlet);

  const cartOutlets = Object.entries(cart)
    .map(([outletFriendlyPath, cartOutlet]) => {
      if (!cartOutlet.stores.length) return;
      return {outletFriendlyPath, cartOutlet};
    })
    .filter(Boolean);

  const handleRemoveOutlet = (outlet: CartOutletsType[0]) => {
    removeCartOutlet(outlet.outletFriendlyPath);
  };

  return (
    <main className="max-h-screen">
      <div className="sticky top-0 left-0 right-0 max-w-md mx-auto h-full z-70">
        <BackBar />
        <div className="h-full">
          {(() => {
            if (!data) {
              return (
                <div className="flex justify-center items-center p-3 pt-10">
                  <MoonLoader loading size={30} speedMultiplier={0.8} />
                </div>
              );
            }

            if (!cartOutlets.length) {
              return (
                <div className="px-4 py-6">
                  <Link href="/" passHref>
                    <a className="bg-white flex flex-col justify-center items-center py-6 rounded-2xl shadow">
                      <img src="/decorators/no-items.svg" alt="no-items" className="w-1/2" />
                      <div className="flex flex-col text-center mt-6 gap-2">
                        <p className="poppins-semibold font-lg">Cart is empty</p>
                        <p className="subTextGray">Click here to start adding some food</p>
                      </div>
                    </a>
                  </Link>
                </div>
              );
            }

            return (
              <section>
                {cartOutlets
                  .sort((a, b) => (a.outletFriendlyPath > b.outletFriendlyPath ? 1 : -1))
                  .map(cartOutlet => {
                    const outletData = data.outlets.find(({friendlyPath}) => friendlyPath === cartOutlet.outletFriendlyPath);
                    if (!outletData) return;
                    return (
                      <CartCard
                        key={cartOutlet.outletFriendlyPath}
                        outletData={outletData}
                        cartOutlet={cartOutlet}
                        handleRemoveOutlet={handleRemoveOutlet}
                        getItemsCount={getOutletItemsCount}
                        href={'/outlets/'}
                      />
                    );
                  })}
              </section>
            );
          })()}
        </div>
      </div>
    </main>
  );
};

export default MyCart;
